/**
 * The authenticator key as a QR code, so a phone can scan it instead of
 * somebody typing thirty-two characters.
 *
 * It encodes the standard `otpauth://totp/…` link every authenticator app
 * reads — Google, Microsoft, Authy, 1Password — carrying the same secret as
 * the key printed beneath it. Drawn here as one SVG path, not fetched from a
 * QR service: the secret must never leave the browser, and the sign-in has to
 * work on an airport network with no internet at all.
 *
 * Black on white with a quiet zone whatever the theme, because that is what a
 * phone camera reads reliably; and never mirrored in Arabic.
 */
import qrcode from "qrcode-generator";
import { useMemo } from "react";
import { t } from "../../i18n";

export function otpauthUri(email: string, secret: string): string {
  return (
    `otpauth://totp/${encodeURIComponent("Sky Logics:" + email)}` +
    `?secret=${secret}&issuer=Sky%20Logics&algorithm=SHA1&period=30&digits=6`
  );
}

export function AuthenticatorQr({ email, secret, size = 196 }: { email: string; secret: string; size?: number }) {
  const { path, count } = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(otpauthUri(email, secret), "Byte");
    qr.make();
    const n = qr.getModuleCount();
    let d = "";
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c + 4} ${r + 4}h1v1h-1z`;
    }
    return { path: d, count: n + 8 };
  }, [email, secret]);

  return (
    <svg
      className="gqr"
      role="img"
      aria-label={t("QR code for your authenticator app")}
      viewBox={`0 0 ${count} ${count}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
    >
      <rect width={count} height={count} fill="#ffffff" />
      <path d={path} fill="#000000" />
    </svg>
  );
}
